import type { TuningSpec } from '../types';

/**
 * Per-bow tuning spec database.
 *
 * Keyed by `${brand}|${model}|${year}` to match the existing bow catalog
 * without forcing a schema migration. Lookups via `getTuningSpec(bow)` —
 * returns `null` when a bow isn't in the database, in which case the wizard
 * falls back to generic ranges + lets the user enter manufacturer specs by hand.
 *
 * Coverage scope: last 4 years of flagship hunting bows from the 8 major
 * brands. We deliberately skip target / Olympic recurves and budget lines.
 *
 * Verification: specs sourced from manufacturer-published tune charts
 * (hoyt.com/pages/tune-charts, mathewsinc.com, pse-archery.com, etc.) and
 * Lancaster Archery Supply spec sheets. Last refreshed May 2026.
 */

type Key = string; // `${brand}|${model}|${year}`

export const TUNING_SPECS: Record<Key, TuningSpec> = {
  // ─────────────────────────────────────────────────────────────────
  // MATHEWS — 2022-2026 flagships, both ATA variants per platform
  // ─────────────────────────────────────────────────────────────────
  'Mathews|V3X 29|2022': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
    notes: 'SwitchWeight cams — module changes shift draw length AND peak weight together. Stay Afield System lets you remove limbs in the field without a press.',
  },
  'Mathews|V3X 33|2022': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
    notes: 'Same SwitchWeight platform as V3X 29 in the longer 33" frame — more forgiving for longer draw lengths.',
  },
  'Mathews|V3X 29|2023': {
    stockCenterShot: 0.8125,  // 13/16"
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
    notes: 'Switchweight cams — module changes shift draw length AND peak weight. Stay Afield System lets you remove limbs without a press.',
  },
  'Mathews|V3X 33|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
    notes: 'Same SwitchWeight platform as V3X 29 but with the longer riser — more forgiving for longer draw lengths.',
  },
  'Mathews|Phase 4 29|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
    notes: 'Resistance Phase Damping in the riser plus the same SwitchWeight cam system as V3X. Quietest Mathews hunting bow at release.',
  },
  'Mathews|Phase 4 33|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
  },
  'Mathews|Lift 29.5|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
    notes: 'Lightest aluminum hunting bow at launch (3.99 lb). 1/4" cam stops give micro draw-length control.',
  },
  'Mathews|Lift 33|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
  },
  'Mathews|Lift X 29.5|2025': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'micro-adjust-screw',
    cableSystem: 'yoke-tunable',
    notes: 'Limb Shift Technology (LST) — micro-tune nock travel via a wrench, no press needed. Major workflow improvement.',
  },
  'Mathews|Lift X 33|2025': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'micro-adjust-screw',
    cableSystem: 'yoke-tunable',
    notes: 'Same LST + SwitchWeight X cams as Lift X 29.5 in the longer 33" frame.',
  },
  'Mathews|ARC 30|2026': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'micro-adjust-screw',
    cableSystem: 'yoke-tunable',
    notes: 'SWX-2 cams with Perimeter Weight Technology. ARC7 limbs. Selectable 80 or 85% let-off on the same module.',
  },
  'Mathews|ARC 34|2026': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'micro-adjust-screw',
    cableSystem: 'yoke-tunable',
  },

  // ─────────────────────────────────────────────────────────────────
  // HOYT — 2023-2026 flagships
  // ─────────────────────────────────────────────────────────────────
  'Hoyt|Carbon RX-7|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
    notes: 'HBX Pro cams — two modules cover the full draw-length range. 100% carbon riser.',
  },
  'Hoyt|Carbon RX-7 Ultra|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
  },
  'Hoyt|VTM 31|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
    notes: 'Aluminum riser counterpart to the carbon RX-7. HBX Pro cams. Quieter than predecessors thanks to limb-pocket vibration damping.',
  },
  'Hoyt|VTM 34|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
  },
  'Hoyt|Alpha X 30|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
    notes: 'Aluminum platform for 2024 — paired with the carbon RX-8. New cam architecture quieter than the VTM line.',
  },
  'Hoyt|Alpha X 33|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
  },
  'Hoyt|Carbon RX-8|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
    notes: 'So smooth and quiet at release that Hoyt removed both the limb and RiserShox dampers. Wider/shorter limbs vs RX-7.',
  },
  'Hoyt|Carbon RX-8 Ultra|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
  },
  'Hoyt|Alpha AX-2 29|2025': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
  },
  'Hoyt|Alpha AX-2 33|2025': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
  },
  'Hoyt|Alpha AX-2 32|2025': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'yoke-tunable',
  },
  // 2026 Alpha AX-3 family — HBX Gen 4 cam + new XTS Tuning System (corrects
  // high AND low tears no-press, unique to 2026 Hoyt). 4 ATA variants.
  'Hoyt|Alpha AX-3 29|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'xts-tunable',
    notes: 'HBX Gen 4 cam. XTS Tuning System lets you correct vertical AND horizontal paper tears without a press — unique to 2026 Hoyt.',
  },
  'Hoyt|Alpha AX-3 33|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'xts-tunable',
    notes: 'Long-ATA variant of the AX-3. Same HBX Gen 4 + XTS as the 29.',
  },
  // (AX-3 SD and 33 LD specialty variants removed — keeping picker clean to AX-3 29 + AX-3 33 + AX-90)
  'Hoyt|Alpha AX-90|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: '90 lb peak draw weight — built for dangerous game + cape buffalo class hunters. Heavier limbs than the AX-3 line.',
  },
  // 2026 Carbon RX-10 family — flagship carbon riser, HBX Gen 4 + XTS. 4 ATA variants.
  'Hoyt|Carbon RX-10|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'xts-tunable',
    notes: 'Carbon riser flagship. HBX Gen 4 + XTS Tuning System — no-press tune for both horizontal AND vertical tears.',
  },
  'Hoyt|Carbon RX-10 Ultra|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'xts-tunable',
    notes: 'Long-ATA carbon — Western/elk hunter favorite. 4.5 lb, 340 fps IBO.',
  },
  // (RX-10 SD and Ultra LD specialty variants removed — keeping picker clean to RX-10 + RX-10 Ultra)

  // ─────────────────────────────────────────────────────────────────
  // PSE — 2023-2026 flagships
  // ─────────────────────────────────────────────────────────────────
  'PSE|Mach 30|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'cam-stop-peg',
    cableSystem: 'snap-spacer',
    notes: 'EC2 cams. EZ.220 Snap Spacers allow shimming without a bow press — PSE\'s big tuning innovation.',
  },
  'PSE|Mach 34|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'cam-stop-peg',
    cableSystem: 'snap-spacer',
  },
  'PSE|EVO XF 30|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'module-swap',
    cableSystem: 'snap-spacer',
    notes: 'Carbon riser. EC2 SF cams. EVO platform tuned for forgiveness over raw speed.',
  },
  'PSE|Mach 33 DS|2025': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'cam-stop-peg',
    cableSystem: 'snap-spacer',
    notes: 'Longer Mach in the lineup. Snap Spacers + Dead-Stop = press-free precision tuning.',
  },
  'PSE|Lightning|2025': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'cam-stop-peg',
    cableSystem: 'snap-spacer',
  },
  'PSE|Sicario FDS 33|2026': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'cam-stop-peg',
    cableSystem: 'snap-spacer',
    notes: 'PSE\'s fastest 2026 carbon bow — 357 IBO, built for speed-first hunters. DFC riser + FDS cam.',
  },
  'PSE|Sicario FDS 35|2026': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'cam-stop-peg',
    cableSystem: 'snap-spacer',
    notes: 'Longer-ATA Sicario sibling — 336 IBO, 35" ATA for tree-stand stability. Same DFC + FDS platform as the 33.',
  },

  // ─────────────────────────────────────────────────────────────────
  // BOWTECH — 2023-2026 flagships
  // ─────────────────────────────────────────────────────────────────
  'Bowtech|SR350|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'shim-tunable',
    notes: 'DeadLock cam shim system — micro-tune cable lean without a press.',
  },
  'Bowtech|CP30|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'shim-tunable',
  },
  'Bowtech|Carbon One|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'shim-tunable',
    notes: 'Carbon riser. Lightest Bowtech yet at launch.',
  },
  'Bowtech|Eva Gen3|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'shim-tunable',
    notes: 'TimeLock — adjustable cable pegs on the cams synchronize timing without a press.',
  },
  'Bowtech|Proven 34|2025': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'shim-tunable',
    notes: 'Bowtech\'s long-ATA 2025 flagship — built for stability at distance.',
  },

  // ─────────────────────────────────────────────────────────────────
  // ELITE — 2023-2026 flagships
  // ─────────────────────────────────────────────────────────────────
  'Elite|EnVision|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'crossover',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'shim-tunable',
    notes: 'SimplexPRO cams — Elite\'s SET tuning system (Simplified Exact Tuning) shifts the cam pos with mods, no press.',
  },
  'Elite|Era|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'crossover',
    drawLengthSystem: 'module-swap',
    cableSystem: 'shim-tunable',
  },
  'Elite|Omnia|2025': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'crossover',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'shim-tunable',
  },
  'Elite|Varos|2026': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'crossover',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'shim-tunable',
    notes: 'Elite\'s 2026 well-rounded flagship — long draw cycle smoothness with SET tuning.',
  },

  // ─────────────────────────────────────────────────────────────────
  // BEAR — 2023-2026 flagships
  // ─────────────────────────────────────────────────────────────────
  'Bear|Refine EKO|2023': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'fixed-yoke',
    notes: 'EKO cam system gives 5 let-off positions on the same module.',
  },
  'Bear|Whitetail Legend|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'fixed-yoke',
  },
  'Bear|Persist|2025': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'fixed-yoke',
  },
  'Bear|Adapt+|2026': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'hybrid',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'fixed-yoke',
    notes: 'Iteration on the Adapt platform with cleaner draw cycle for 2026.',
  },

  // ─────────────────────────────────────────────────────────────────
  // PRIME — 2023-2026 flagships
  // ─────────────────────────────────────────────────────────────────
  'Prime|Revex 2|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'fixed-yoke',
    notes: 'Centergy parallel-cam system eliminates cam lean by design — much less yoke tuning needed.',
  },
  'Prime|Revex 4|2024': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'fixed-yoke',
  },
  'Prime|InLine 1|2022': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'fixed-yoke',
    notes: 'Centergy InLine cam stack puts buss + string in the same plane — eliminates lateral torque.',
  },
  'Prime|InLine 3|2022': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'fixed-yoke',
  },
  'Prime|InLine 5|2022': {
    stockCenterShot: 0.8125,
    centerShotTolerance: 0.0625,
    tillerTarget: 0,
    tillerTolerance: 0.125,
    camArchitecture: 'dual-binary',
    drawLengthSystem: 'rotating-module',
    cableSystem: 'fixed-yoke',
  },

  // ─────────────────────────────────────────────────────────────────
  // PSE additions — full Mach lineup + EVO XF + Lightning + Sicario
  // ─────────────────────────────────────────────────────────────────
  'PSE|EVO XF 33|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'module-swap', cableSystem: 'snap-spacer',
    notes: 'Carbon riser. EC2 SF cams. Long-ATA EVO for stability-first shooters.',
  },
  'PSE|Mach 33|2023': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'cam-stop-peg', cableSystem: 'snap-spacer',
    notes: 'EC2 cams + Snap Spacers — PSE\'s sweet-spot mid-ATA hunter.',
  },
  'PSE|Mach 30 DS|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'cam-stop-peg', cableSystem: 'snap-spacer',
    notes: 'Dead-Stop adds harder valley wall + tighter back-wall feel vs base Mach 30.',
  },
  'PSE|Mach 30 DS|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'cam-stop-peg', cableSystem: 'snap-spacer',
    notes: 'Now ships with FDS cam — +10 fps over EC2 era. Adjustable 70-85% let-off.',
  },
  'PSE|Mach 33 DS|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'cam-stop-peg', cableSystem: 'snap-spacer',
    notes: 'FDS cam upgrade for 2026. Same Snap Spacer + Dead-Stop tuning workflow as Mach 30.',
  },

  // ─────────────────────────────────────────────────────────────────
  // BOWTECH additions — SR350, Solution SS, Commander, Eva Gen3
  // ─────────────────────────────────────────────────────────────────
  'Bowtech|Solution SS|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
    notes: 'DeadLock cam system — shim micro-tune cable lean without a press.',
  },
  'Bowtech|Commander|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
  },
  'Bowtech|Reckoning Gen2 SD|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
    notes: 'Long-ATA stability-first hunter with the DeadLock system.',
  },
  'Bowtech|Core SS|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
  },
  'Bowtech|Core SS|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
  },
  'Bowtech|SR|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
    notes: 'Speed-first Bowtech. DeadLock + TimeLock means no-press tuning end to end.',
  },
  'Bowtech|Proven|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
  },
  'Bowtech|Honor|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
  },
  'Bowtech|Solution|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
  },

  // ─────────────────────────────────────────────────────────────────
  // ELITE additions — Ethos, Exalt 35, Veros, Carbon Eon
  // ─────────────────────────────────────────────────────────────────
  'Elite|Carbon Eon|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'crossover', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
    notes: 'Carbon riser with SP2 cam. SET tuning system, no-press cam position shifts.',
  },
  'Elite|Ethos|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'crossover', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
  },
  'Elite|Exalt|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'crossover', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
  },
  'Elite|Exalt 35|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'crossover', drawLengthSystem: 'rotating-module', cableSystem: 'shim-tunable',
    notes: 'Longest-ATA Elite — for target archers + long-draw hunters who want maximum stability.',
  },

  // ─────────────────────────────────────────────────────────────────
  // BEAR additions — Refine, Whitetail family, Alaskan, Persist, Legend 30
  // ─────────────────────────────────────────────────────────────────
  'Bear|Refine EKO|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
    notes: 'EKO cam gives 4 let-off positions on the same module.',
  },
  'Bear|Whitetail Legend Pro|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Bear|Whitetail Legend|2023': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Bear|Alaskan|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
    notes: 'Long-ATA platform aimed at backcountry hunters — extra forgiveness on uneven stances.',
  },
  'Bear|Whitetail Maxx|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Bear|Persist 33|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
    notes: 'Long-ATA flagship with built-in sling + pull-up loop for treestand hunters.',
  },
  'Bear|Legend 30|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Bear|Whitetail INT|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
    notes: 'Upgrade of Whitetail Maxx with EKO cams. Available as RTH (ready-to-hunt) package.',
  },
  // (Bear|Adapt+|2026 defined in first BEAR block above)

  // ─────────────────────────────────────────────────────────────────
  // PRIME additions — Revex 6
  // ─────────────────────────────────────────────────────────────────
  'Prime|Revex 6|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'dual-binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
    notes: 'Longest Revex — for target shooters + long-draw hunters. Centergy InLine cam stack.',
  },

  // ─────────────────────────────────────────────────────────────────
  // HOYT additions — RX-9 lineup, Carbon RX-9 Ultra, AX-3 33
  // ─────────────────────────────────────────────────────────────────
  'Hoyt|RX-9|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'Aluminum riser version of the Carbon RX-9 — same HBX cam architecture.',
  },
  'Hoyt|Carbon RX-9|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Hoyt|Carbon RX-9 Ultra|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'Long-ATA carbon flagship — Western/elk hunter favorite.',
  },
  // (Hoyt|Alpha AX-3 33|2026 defined above in the 2026 family block — uses XTS Tuning System)

  // ─────────────────────────────────────────────────────────────────
  // DARTON — Sequel + TriTech families
  // ─────────────────────────────────────────────────────────────────
  'Darton|Sequel 31|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
    notes: 'Darton DualSync cam. Made in Michigan, beloved by mechanical-minded tuners.',
  },
  'Darton|Sequel 33|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Darton|Sequel|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Darton|TriTech 31|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
    notes: 'TriTech cam — three-track design that decouples cable/string for cleaner nock travel.',
  },
  'Darton|TriTech 33|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Darton|TriTech 35|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },

  // ─────────────────────────────────────────────────────────────────
  // HOYT 2026 — SD (Short Draw) / LD (Long Draw) module variants
  // Same riser + cam architecture as the parent model; different module set.
  // ─────────────────────────────────────────────────────────────────
  'Hoyt|Alpha AX-3 29 SD (Short Draw)|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'xts-tunable',
    notes: 'Short-draw module set (23-27" DL) — same HBX Gen 4 + XTS as the standard AX-3 29.',
  },
  'Hoyt|Alpha AX-3 33 LD (Long Draw)|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'xts-tunable',
    notes: 'Long-draw module set (31.25-33" DL) for taller archers on the 33" ATA platform.',
  },
  'Hoyt|Carbon RX-10 SD (Short Draw)|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'xts-tunable',
    notes: 'Short-draw module set (23-27" DL) on the carbon RX-10 platform.',
  },
  'Hoyt|Carbon RX-10 Ultra LD (Long Draw)|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0, tillerTolerance: 0.125,
    camArchitecture: 'hybrid', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'xts-tunable',
    notes: 'Long-draw module set (29-32.5" DL) on the long-ATA carbon Ultra.',
  },

  // ═════════════════════════════════════════════════════════════════════════
  // TARGET COMPOUND BOWS — tuning specs
  // ═════════════════════════════════════════════════════════════════════════
  // Target bows generally run:
  //   - 65% let-off (with options for 75% and "tweener" 67.5/72.5)
  //   - Tiller set positive 1/8" - 1/4" (top tiller LONGER than bottom) for
  //     vertical-pull release — different from hunting's 0 (matched).
  //   - Center shot 13/16" stock, but some setups push to 7/8" for slightly
  //     more rest clearance with overdraws.
  //   - SCTR / SWT cams are smoother than hunting binaries.
  // ═════════════════════════════════════════════════════════════════════════

  // Mathews TRX (target)
  'Mathews|TRX 34|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'Crosscentric cam + AVS (anti-vibration). 65% let-off stock. Positive 1/8" tiller for target release.',
  },
  'Mathews|TRX 36|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Mathews|TRX 38 G2|2023': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'Gen 2 Crosscentric cam — flagship Vegas + 720 round bow for many pro shooters.',
  },
  'Mathews|TRX 40 G2|2023': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  // Mathews TITLE (target, 2025+ SWT cam)
  'Mathews|TITLE 34|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'yoke-tunable',
    notes: 'SWT cam — Switch Weight Technology. Micro draw-length adjust without module swap.',
  },
  'Mathews|TITLE 36|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'yoke-tunable',
  },
  'Mathews|TITLE 38|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'yoke-tunable',
  },
  'Mathews|TITLE 40|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'yoke-tunable',
  },
  'Mathews|TITLE 34|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'yoke-tunable',
    notes: 'SWT-2 cam refinement. Industry-leading Vegas / WA720 platform.',
  },
  'Mathews|TITLE 36|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'yoke-tunable',
  },
  'Mathews|TITLE 38|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'yoke-tunable',
  },
  'Mathews|TITLE 40|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'micro-adjust-screw', cableSystem: 'yoke-tunable',
  },

  // Hoyt Target — Pro Force / Pro Comp Elite / Concept series
  'Hoyt|Pro Force FX|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'Aluminum target riser — classic Hoyt Cam & 1/2+ system.',
  },
  'Hoyt|Pro Comp Elite FX|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'Long-ATA target flagship — Cam & 1/2+ for the most repeatable wall in the lineup.',
  },
  'Hoyt|Concept FX 34|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'SCTR cam — combines Cam & 1/2 and HB cam DNA. Modular Grip system (0°/4°/8°).',
  },
  'Hoyt|Concept X 37|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'SCTR cam. Most rotationally-stable riser Hoyt has built. Spec/CDM modules.',
  },
  'Hoyt|Concept X 40|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'Long-ATA target flagship — picked by Vegas + WA720 podium shooters.',
  },
  'Hoyt|Concept X 37|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Hoyt|Concept X 40|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Hoyt|Concept X 37|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Hoyt|Concept X 40|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Hoyt|Concept FX|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'hybrid', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },

  // PSE Target
  'PSE|Supra Focus XL|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'module-swap', cableSystem: 'snap-spacer',
    notes: 'Long-running PSE target platform. EZ.220 snap-spacer tuning saves press time.',
  },
  'PSE|Supra EXT 33|2023': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'module-swap', cableSystem: 'snap-spacer',
  },
  'PSE|Supra EXT 37|2023': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'module-swap', cableSystem: 'snap-spacer',
  },
  'PSE|Supra EXT 40|2024': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'module-swap', cableSystem: 'snap-spacer',
  },
  'PSE|Citation 40|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'module-swap', cableSystem: 'snap-spacer',
    notes: 'PSE\'s newest dedicated 40" target — Citation cam tuned for forgiveness over speed.',
  },
  'PSE|Citation 40|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'module-swap', cableSystem: 'snap-spacer',
  },
  'PSE|Supra X 37|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'module-swap', cableSystem: 'snap-spacer',
  },

  // Bowtech Target
  'Bowtech|Specialist II 38|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'OverDrive Binary cam. Bowtech\'s long-running target flagship.',
  },
  'Bowtech|Specialist II 40|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Bowtech|Patriot 38|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Bowtech|Patriot 40|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Bowtech|Patriot 38|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Bowtech|Patriot 40|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },

  // Elite Target
  'Elite|Victory 37|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'SimplexPRO cam + SET tuning system. 75% let-off stock — split-the-difference target/field.',
  },
  'Elite|Victory 40|2023': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Elite|Verdict 37|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
    notes: 'Verdict cam — Elite\'s dedicated target lineup. SET v2 tuning. 65/70/75 let-off.',
  },
  'Elite|Verdict 40|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Elite|Verdict 37|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },
  'Elite|Verdict 40|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'yoke-tunable',
  },

  // Prime Target
  'Prime|Logic CT3|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
    notes: 'Prime\'s parallel-cam architecture eliminates cam lean. CT3 (36" ATA) is short-leg target.',
  },
  'Prime|Logic CT5|2022': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
    notes: 'Long-ATA CT5 — pure target. 65% let-off stock.',
  },
  'Prime|Nexus 2|2023': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Prime|Nexus 4|2023': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Prime|Form 38|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
    notes: 'Form cam — newest Prime target system. Parallel-cam DNA + refined wall.',
  },
  'Prime|Form 40|2025': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Prime|Form 38|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
  'Prime|Form 40|2026': {
    stockCenterShot: 0.8125, centerShotTolerance: 0.0625, tillerTarget: 0.125, tillerTolerance: 0.0625,
    camArchitecture: 'binary', drawLengthSystem: 'rotating-module', cableSystem: 'fixed-yoke',
  },
};

/**
 * Lookup a bow's tuning spec. Returns null if the bow isn't in the database;
 * caller should fall back to generic ranges + a "manual entry" UI.
 */
export function getTuningSpec(brand: string, model: string, year: number): TuningSpec | null {
  return TUNING_SPECS[`${brand}|${model}|${year}`] ?? null;
}
